/**
 * Profile - Modernized Read-Only Account Page
 * 
 * Provides a clean, aesthetic view of User and Wialon Account information.
 * Features:
 * - Glassmorphism design
 * - Read-only data display
 * - Real-time Wialon stats integration
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Cookies from "universal-cookie";
import { keyframes } from '@emotion/react';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import InfoIcon from '@mui/icons-material/Info';

import profileAPI from "../../Network/Profile/index";
import { getAccountStats } from '../../Network/Wialon/WialonAPI';
import userImage from '../../assets/img/user.png';
import '../../styles/ModernStyles.css';

const cookies = new Cookies();

// ── Animations ───────────────────────────────────────────────────────
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ── Styled Components ────────────────────────────────────────────────
const ProfileCard = styled(Paper)(({ theme }) => ({
  padding: '32px',
  borderRadius: '24px',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
  animation: `${fadeInUp} 0.6s ease-out forwards`,
}));

const InfoItem = ({ icon, label, value, color = '#4ECDC4' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
    <Box sx={{
      width: 48,
      height: 48,
      borderRadius: '12px',
      background: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
    </Box>
    <Box>
      <Typography sx={{
        fontSize: 12,
        color: '#94A3B8',
        fontWeight: 600,
        textTransform: 'uppercase',
        fontFamily: 'Cairo, sans-serif'
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 16,
        color: '#1E293B',
        fontWeight: 600,
        fontFamily: 'Cairo, sans-serif'
      }}>
        {((value == 0) ? "0" : value) || '---'}
      </Typography>
    </Box>
  </Box>
);

export default function Profile() {
  const [t] = useTranslation('common');
  const directions = useSelector((state) => state.settingsData.directions);
  const isRTL = directions === 'rtl';

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const hasFetched = useRef(false);

  // Fetch Profile & Wialon Data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // 1. Fetch User Info
      const params = {
        username: cookies.get("user_name"),
        user_id: cookies.get("user_id"),
        account_name: cookies.get("account_name"),
        account_id: cookies.get("account_id"),
        sid: cookies.get("sid"),
        baseurl: cookies.get("baseUrl"),
      };

      const profileRes = await profileAPI.Profile({ params });
      if (profileRes.status && profileRes.data?.data) {
        setUser(profileRes.data.data);
      }

      // 2. Fetch Wialon Stats
      const statsRes = await getAccountStats();
      if (statsRes.status && statsRes.data?.data) {
        const actualData = statsRes.data.data;
        setStats({
          total: actualData.units ?? 0,
          active: actualData.active_units ?? 0,
          accountStatus: actualData.account_status?.active ? (t('WELCOME.activeStatus') || 'Active') : (t('WELCOME.blockedStatus') || 'Blocked'),
          daysLeft: actualData.account_status?.days_left ?? 0,
          isActive: actualData.account_status?.active
        });
      }

    } catch (error) {
      console.error('[Profile] Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#4ECDC4' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 2, md: 4 },
      background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 100%)',
      minHeight: 'calc(100vh - 100px)',
      borderRadius: 4
    }}>
      <Grid container spacing={4} sx={{ maxWidth: 1200, mx: 'auto' }}>

        {/* Header Section */}
        <Grid item xs={12}>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3, animation: `${fadeInUp} 0.5s ease-out` }}>
            <Avatar
              src={user?.image_path ? `${process.env.REACT_APP_API_URL}uploads/users/${user.image_path}` : userImage}
              sx={{ width: 100, height: 100, border: '4px solid #fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            />
            <Box>
              <Typography sx={{ fontSize: 32, fontWeight: 800, color: '#1E293B', fontFamily: 'Cairo, sans-serif' }}>
                {user?.name || cookies.get('user_name')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={cookies.get('account_name')}
                  size="small"
                  sx={{ background: '#4ECDC415', color: '#4ECDC4', fontWeight: 600, fontFamily: 'Cairo, sans-serif' }}
                />
                <Typography sx={{ fontSize: 14, color: '#64748B', fontFamily: 'Cairo, sans-serif' }}>
                  {t('PROFILE.MEMBER') || 'Member'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* User Information Card */}
        <Grid item xs={12} md={6}>
          <ProfileCard>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1E293B', mb: 4, fontFamily: 'Cairo, sans-serif', borderRight: isRTL ? '4px solid #4ECDC4' : 'none', borderLeft: !isRTL ? '4px solid #4ECDC4' : 'none', px: 2 }}>
              {t('PROFILE.USER_TITLE') || 'Personal Information'}
            </Typography>

            <InfoItem
              icon={<PersonIcon />}
              label={t('PROFILE.FULL_NAME') || 'Full Name'}
              value={user?.name}
              color="#4ECDC4"
            />
            <InfoItem
              icon={<BadgeIcon />}
              label={t('PROFILE.USERNAME') || 'Username'}
              value={cookies.get('user_name')}
              color="#6C9BCF"
            />
            <InfoItem
              icon={<PhoneIcon />}
              label={t('PROFILE.PHONE') || 'Phone Number'}
              value={user?.phone}
              color="#F59E0B"
            />
          </ProfileCard>
        </Grid>

        {/* Account Statistics Card */}
        <Grid item xs={12} md={6}>
          <ProfileCard sx={{ animationDelay: '0.1s' }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1E293B', mb: 4, fontFamily: 'Cairo, sans-serif', borderRight: isRTL ? '4px solid #667EEA' : 'none', borderLeft: !isRTL ? '4px solid #667EEA' : 'none', px: 2 }}>
              {t('WELCOME.accountStatistics') || 'Account Statistics'}
            </Typography>

            <InfoItem
              icon={<BusinessIcon />}
              label={t('WELCOME.accountName') || 'Account Name'}
              value={cookies.get('account_name')}
              color="#667EEA"
            />
            <InfoItem
              icon={<FingerprintIcon />}
              label={t('WELCOME.accountID') || 'Account ID'}
              value={cookies.get('account_id')}
              color="#94A3B8"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InfoItem
                  icon={<DirectionsCarIcon />}
                  label={t('WELCOME.totalVehicles') || 'Total Units'}
                  value={stats?.total}
                  color="#10B981"
                />
              </Grid>
              <Grid item xs={6}>
                <InfoItem
                  icon={<InfoIcon />}
                  label={t('WELCOME.activeUnits') || 'Active'}
                  value={stats?.active}
                  color="#FF6B6B"
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2, opacity: 0.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InfoItem
                  icon={<AccessTimeIcon />}
                  label={t('WELCOME.daysLeft') || 'Days Left'}
                  value={stats?.daysLeft ?? "0"}
                  color="#6C9BCF"
                />
              </Grid>
              {/* <Grid item xs={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, fontFamily: 'Cairo, sans-serif' }}>
                    {t('WELCOME.systemStatus') || 'Status'}
                  </Typography>
                  <Chip
                    label={stats?.accountStatus}
                    size="small"
                    sx={{
                      background: stats?.isActive ? '#10B98115' : '#EF444415',
                      color: stats?.isActive ? '#10B981' : '#EF4444',
                      fontWeight: 700,
                      fontFamily: 'Cairo, sans-serif',
                      width: 'fit-content'
                    }}
                  />
                </Box>
              </Grid> */}
            </Grid>
          </ProfileCard>
        </Grid>

      </Grid>
    </Box>
  );
}
